// Intro effect: the room assembles itself outward from the visitor. An arrival field (see
// arrival-field.js) says when the build-up front reaches each point: it starts at the surfaces closest
// to the visitor above, below and to the sides, then races along walls, floor and ceiling by the
// shortest path; a ceiling lamp starts at its mount and builds downwards. Each splat starts with a
// Gaussian-distributed random delay around its arrival time, grows from a speck into its shape, and
// the front slows down towards the end so the last splats settle softly. World space throughout.
import { ADDRESS_CLAMP_TO_EDGE, Color, FILTER_NEAREST, PIXELFORMAT_RGBA8, Texture } from 'playcanvas';
import { GSplatShaderEffect } from 'playcanvas/scripts/esm/gsplat/gsplat-shader-effect.mjs';

import { packArrivalField } from './arrival-field.js';

const glsl = /* glsl */`
uniform float uTime;
uniform float uDuration;
uniform float uCurve;
uniform float uJitter;
uniform float uGrow;
uniform vec3 uTint;
uniform sampler2D uField;
uniform vec3 uFieldMin;
uniform float uFieldCell;
uniform vec3 uFieldDims;

float g_e;
float g_r;

float bHash(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

float fieldAt(vec3 i) {
    i = clamp(i, vec3(0.0), uFieldDims - 1.0);
    vec2 uv = vec2((i.x + 0.5) / uFieldDims.x, (i.y * uFieldDims.z + i.z + 0.5) / (uFieldDims.y * uFieldDims.z));
    vec4 t = textureLod(uField, uv, 0.0);
    return (t.r * 65280.0 + t.g * 255.0) / 65535.0;
}

float arrival(vec3 p) {
    vec3 c = (p - uFieldMin) / uFieldCell - 0.5;
    vec3 i = floor(c);
    vec3 f = c - i;
    float a = mix(fieldAt(i), fieldAt(i + vec3(1.0, 0.0, 0.0)), f.x);
    float b = mix(fieldAt(i + vec3(0.0, 1.0, 0.0)), fieldAt(i + vec3(1.0, 1.0, 0.0)), f.x);
    float c0 = mix(fieldAt(i + vec3(0.0, 0.0, 1.0)), fieldAt(i + vec3(1.0, 0.0, 1.0)), f.x);
    float d = mix(fieldAt(i + vec3(0.0, 1.0, 1.0)), fieldAt(i + vec3(1.0, 1.0, 1.0)), f.x);
    return mix(mix(a, b, f.y), mix(c0, d, f.y), f.z);
}

void modifySplatCenter(inout vec3 center) {
    g_r = bHash(center * 13.7);
    // Sum of four uniforms: roughly Gaussian delay, centred on the arrival time
    float gauss = (g_r + bHash(center * 7.3) + bHash(center * 3.1) + bHash(center * 19.9)) * 0.25 - 0.5;
    float a = arrival(center);
    float start = pow(a, uCurve) * uDuration + uJitter * (1.0 + 2.0 * gauss);
    float p = clamp((uTime - start) / (uGrow * (1.0 + 1.2 * a)), 0.0, 1.0);
    g_e = 1.0 - pow(1.0 - p, 3.0);
    if (g_e <= 0.0 || g_e >= 1.0) return;
    float ang = g_r * 6.2831;
    center += vec3(cos(ang), bHash(center * 5.1) - 0.5, sin(ang)) * 0.035 * (1.0 - g_e);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    if (g_e >= 1.0) return;
    if (g_e <= 0.0) { scale = vec3(0.0); return; }
    float size = gsplatGetSizeFromScale(scale);
    scale = mix(vec3(min(size, 0.012)), scale, smoothstep(0.1, 1.0, g_e));
}

void modifySplatColor(vec3 center, inout vec4 color) {
    if (g_e >= 1.0 || g_e <= 0.0) return;
    float k = 1.0 - g_e;
    color.rgb += uTint * k * k;
    color.a *= smoothstep(0.0, 0.3, g_e);
}
`;

const wgsl = /* wgsl */`
uniform uTime: f32;
uniform uDuration: f32;
uniform uCurve: f32;
uniform uJitter: f32;
uniform uGrow: f32;
uniform uTint: vec3f;
uniform uFieldMin: vec3f;
uniform uFieldCell: f32;
uniform uFieldDims: vec3f;
var uField: texture_2d<f32>;
var uFieldSampler: sampler;

var<private> g_e: f32;
var<private> g_r: f32;

fn bHash(p: vec3f) -> f32 {
    return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

fn fieldAt(i0: vec3f) -> f32 {
    let i = clamp(i0, vec3f(0.0), uniform.uFieldDims - 1.0);
    let uv = vec2f((i.x + 0.5) / uniform.uFieldDims.x, (i.y * uniform.uFieldDims.z + i.z + 0.5) / (uniform.uFieldDims.y * uniform.uFieldDims.z));
    let t = textureSampleLevel(uField, uFieldSampler, uv, 0.0);
    return (t.r * 65280.0 + t.g * 255.0) / 65535.0;
}

fn arrival(p: vec3f) -> f32 {
    let c = (p - uniform.uFieldMin) / uniform.uFieldCell - 0.5;
    let i = floor(c);
    let f = c - i;
    let a = mix(fieldAt(i), fieldAt(i + vec3f(1.0, 0.0, 0.0)), f.x);
    let b = mix(fieldAt(i + vec3f(0.0, 1.0, 0.0)), fieldAt(i + vec3f(1.0, 1.0, 0.0)), f.x);
    let c0 = mix(fieldAt(i + vec3f(0.0, 0.0, 1.0)), fieldAt(i + vec3f(1.0, 0.0, 1.0)), f.x);
    let d = mix(fieldAt(i + vec3f(0.0, 1.0, 1.0)), fieldAt(i + vec3f(1.0, 1.0, 1.0)), f.x);
    return mix(mix(a, b, f.y), mix(c0, d, f.y), f.z);
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
    let p0 = *center;
    g_r = bHash(p0 * 13.7);
    let gauss = (g_r + bHash(p0 * 7.3) + bHash(p0 * 3.1) + bHash(p0 * 19.9)) * 0.25 - 0.5;
    let a = arrival(p0);
    let start = pow(a, uniform.uCurve) * uniform.uDuration + uniform.uJitter * (1.0 + 2.0 * gauss);
    let p = clamp((uniform.uTime - start) / (uniform.uGrow * (1.0 + 1.2 * a)), 0.0, 1.0);
    g_e = 1.0 - pow(1.0 - p, 3.0);
    if (g_e <= 0.0 || g_e >= 1.0) {
        return;
    }
    let ang = g_r * 6.2831;
    *center = p0 + vec3f(cos(ang), bHash(p0 * 5.1) - 0.5, sin(ang)) * 0.035 * (1.0 - g_e);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    if (g_e >= 1.0) {
        return;
    }
    if (g_e <= 0.0) {
        *scale = vec3f(0.0);
        return;
    }
    let size = gsplatGetSizeFromScale(*scale);
    *scale = mix(vec3f(min(size, 0.012)), *scale, smoothstep(0.1, 1.0, g_e));
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    if (g_e >= 1.0 || g_e <= 0.0) {
        return;
    }
    let k = 1.0 - g_e;
    *color = vec4f((*color).rgb + uniform.uTint * k * k, (*color).a * smoothstep(0.0, 0.3, g_e));
}
`;

export class GSplatRevealBuild extends GSplatShaderEffect {
    static scriptName = 'gsplatRevealBuild';

    /** Seconds for the front to cover the whole arrival field. */
    duration = 3.2;
    /** > 1: the front starts fast and slows down towards the end. */
    curve = 1.5;
    /** Spread (s) of the per-splat random delay. */
    jitter = 0.22;
    /** Seconds each splat needs to form (longer for late splats). */
    grow = 0.6;
    /** Additive glow while a splat is forming. */
    tint = new Color(0.25, 0.22, 0.18);

    _tint = [0, 0, 0];
    _min = [0, 0, 0];
    _dims = [1, 1, 1];
    _texture = null;
    _field = null;

    /** @param {ReturnType<import('./arrival-field.js').computeArrivalField>} field */
    setField(field) {
        const packed = packArrivalField(field);
        this._texture?.destroy();
        this._texture = new Texture(this.app.graphicsDevice, {
            name: 'revealArrival',
            width: packed.width,
            height: packed.height,
            format: PIXELFORMAT_RGBA8,
            mipmaps: false,
            minFilter: FILTER_NEAREST,
            magFilter: FILTER_NEAREST,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        this._texture.lock().set(packed.data);
        this._texture.unlock();
        this._field = field;
        this._min = field.min.slice();
        this._dims = [field.nx, field.ny, field.nz];
    }

    getShaderGLSL() {
        return glsl;
    }

    getShaderWGSL() {
        return wgsl;
    }

    getCompletionTime() {
        return this.duration + this.jitter * 2 + this.grow * 2.2;
    }

    updateEffect(effectTime) {
        if (effectTime >= this.getCompletionTime() || !this._texture) {
            if (this._texture) this.enabled = false;
            return;
        }
        this.setUniform('uTime', effectTime);
        this.setUniform('uDuration', this.duration);
        this.setUniform('uCurve', this.curve);
        this.setUniform('uJitter', this.jitter);
        this.setUniform('uGrow', this.grow);
        this._tint[0] = this.tint.r; this._tint[1] = this.tint.g; this._tint[2] = this.tint.b;
        this.setUniform('uTint', this._tint);
        this.setUniform('uField', this._texture);
        this.setUniform('uFieldMin', this._min);
        this.setUniform('uFieldCell', this._field.cell);
        this.setUniform('uFieldDims', this._dims);
    }

    destroy() {
        super.destroy();
        this._texture?.destroy();
        this._texture = null;
    }
}
